/**
 * OverlayRegistry - Plugin system for screen share video overlays
 *
 * Allows features to register custom overlay renderers that will be
 * displayed on top of the screen share video.
 *
 * Usage:
 * ```javascript
 * // In your feature initialization
 * import { overlayRegistry } from '../components/overlays/OverlayRegistry';
 * import MyOverlay from './MyOverlay';
 *
 * overlayRegistry.register('my-feature', MyOverlay, {
 *   priority: 100,  // Higher = renders on top
 *   name: 'My Feature Overlay'
 * });
 *
 * // Later, to unregister
 * overlayRegistry.unregister('my-feature');
 * ```
 */

class OverlayRegistry {
  constructor() {
    this.overlays = new Map();
    this.listeners = new Set();
  }

  /**
   * Register an overlay component
   * @param {string} id - Unique identifier for the overlay
   * @param {React.Component} component - React component to render
   * @param {Object} options - Configuration options
   * @param {number} options.priority - Render order (higher = on top)
   * @param {string} options.name - Human-readable name
   * @param {boolean} options.enabled - Whether overlay is active (default: true)
   */
  register(id, component, options = {}) {
    if (this.overlays.has(id)) {
      console.warn(`[OverlayRegistry] Overlay '${id}' already registered, replacing`);
    }

    this.overlays.set(id, {
      id,
      component,
      priority: options.priority || 0,
      name: options.name || id,
      enabled: options.enabled !== false
    });

    console.log(`[OverlayRegistry] Registered overlay: ${id}`);
    this.notifyListeners();
  }

  /**
   * Unregister an overlay
   * @param {string} id - Overlay identifier
   */
  unregister(id) {
    if (this.overlays.delete(id)) {
      console.log(`[OverlayRegistry] Unregistered overlay: ${id}`);
      this.notifyListeners();
    }
  }

  /**
   * Enable or disable an overlay
   * @param {string} id - Overlay identifier
   * @param {boolean} enabled - Whether to enable
   */
  setEnabled(id, enabled) {
    const overlay = this.overlays.get(id);
    if (overlay) {
      overlay.enabled = enabled;
      this.notifyListeners();
    }
  }

  /**
   * Get all registered overlays, sorted by priority
   * @param {boolean} enabledOnly - Only return enabled overlays
   * @returns {Array} Array of overlay configs
   */
  getOverlays(enabledOnly = true) {
    const overlays = Array.from(this.overlays.values());

    if (enabledOnly) {
      return overlays
        .filter(o => o.enabled)
        .sort((a, b) => a.priority - b.priority);
    }

    return overlays.sort((a, b) => a.priority - b.priority);
  }

  /**
   * Subscribe to overlay changes
   * @param {Function} callback - Called when overlays change
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify listeners of changes
   */
  notifyListeners() {
    const overlays = this.getOverlays();
    for (const callback of this.listeners) {
      try {
        callback(overlays);
      } catch (error) {
        console.error('[OverlayRegistry] Error in listener:', error);
      }
    }
  }
}

// Singleton instance
export const overlayRegistry = new OverlayRegistry();

export default OverlayRegistry;
